import axios from 'axios';
import qs from 'qs';

import httpService from 'services/httpService';

const BASE_URL = '/api/v1/discovery/organizations';
const BASE_V3_URL = '/api/v3/discovery/organizations';
const BASE_BACKOFFICE_URL = '/api/v3/organizations-backoffice/organizations';

interface FetchOrgEventsInput {
  organizationId: string;
  query: {
    limit: number;
    offset: number;
    search?: string | null;
    sortBy?: 'asc' | 'desc';
    excludeUnpulishedEvents?: boolean;
  };
}

interface FetchOrgDocsInput {
  organizationId?: string;
  eventId?: string;
  query: {
    limit?: number;
    offset?: number;
    search?: string | null;
    sort?: string;
  };
}

enum FilterConfigCase {
  EventPosters = 'event_posters',
  OrgPosters = 'org_posters',
}
export const fetchOrganizations = (search: string, limit = 20, offset = 0) =>
  httpService
    .get<{ organizations: MorressierOrganization[]; total: number }>({
      url: `${BASE_BACKOFFICE_URL}?search=${search || ''}&offset=${offset}&limit=${limit}`,
    })
    .then(response => response.data.organizations);

/**
 * HTTP request to get organization params
 */
export const getOrgPage = (orgSlug: string) =>
  httpService
    .get<MorressierOrganization>({ url: `${BASE_V3_URL}/${orgSlug}` })
    .then(({ data }) => data);

/**
 * Fetch organization and other data
 */
export const withOrg = <T extends unknown>(orgId: string, otherFetch: () => Promise<T>) =>
  Promise.all([getOrgPage(orgId), otherFetch()]);

export const getOrgStats = (orgSlug: string) =>
  httpService
    .get<OrganizationStats>({ url: `${BASE_URL}/${orgSlug}/stats` })
    .then(({ data }) => data);

export const fetchOrgEvents = (input: FetchOrgEventsInput): Promise<OrganizationEventsResponse> => {
  const queryParams = qs.stringify(input.query);
  return httpService
    .get<OrganizationEventsResponse>({
      url: `${BASE_URL}/${input.organizationId}/events?${queryParams}`,
    })
    .then(response => response.data);
};

export const fetchOrgEventsV3 = (input: FetchOrgEventsInput) =>
  httpService
    .get<OrganizationEventsResponseV3>({
      url: `${BASE_V3_URL}/${input.organizationId}/events?${qs.stringify(input.query)}`,
    })
    .then(response => response.data);

export const updateOrg = (
  id: string,
  data: Partial<Pick<MorressierOrganization, 'country' | 'city' | 'description' | 'brand_theme'>>,
) =>
  httpService
    .patch<OrganizationEventsResponseV3>({
      url: `${BASE_BACKOFFICE_URL}/${id}`,
      data,
    })
    .then(response => response.data);

const uploadFile = (file: File, signedUrl: string) =>
  axios({
    url: signedUrl,
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'Cache-Control': 'public, max-age=86400',
    },
    data: file,
  });

type SignedUrlResponse = {
  file_url: string;
  signed_url: string;
};
const getUploadOrgUrl = (imageKind: 'banner' | 'logo') => (id: string, extension: string) =>
  httpService
    .get<SignedUrlResponse>({
      url: `${BASE_BACKOFFICE_URL}/${id}/${imageKind}/upload`,
      params: {
        extension,
      },
    })
    .then(response => response.data);

export const getUploadOrgBannerUrl = getUploadOrgUrl('banner');
export const getUploadOrgLogoUrl = getUploadOrgUrl('logo');

const getExtension = (filename: string) => filename.match(/\.([^.]+?)$/)?.[1] ?? '';

export async function updateBanner(orgId: string, file: File) {
  const signedResp = await getUploadOrgBannerUrl(orgId, getExtension(file.name));
  await uploadFile(file, signedResp.signed_url);

  // TODO: come up with something better than mongo $set
  await updateOrg(orgId, { 'brand_theme.banner_url': signedResp.file_url } as any);

  return signedResp.file_url;
}

export async function updateLogo(orgId: string, file: File) {
  const signedResp = await getUploadOrgLogoUrl(orgId, getExtension(file.name));
  await uploadFile(file, signedResp.signed_url);

  // TODO: come up with something better than mongo $set
  await updateOrg(orgId, { 'brand_theme.logo_url': signedResp.file_url } as any);
}

export const fetchOrgDocuments = (input: FetchOrgDocsInput) => {
  const queryParams = qs.stringify(input.query);
  return httpService
    .get<OrganizationDocumentsResponse>({
      url: `${BASE_URL}/${input.organizationId}/documents?${queryParams}`,
    })
    .then(response => response.data);
};

export const fetchOrgDocumentsV2 = (
  input: FetchOrgDocsInput,
): Promise<OrganizationDocumentsResponse> => {
  const queryParams = qs.stringify({
    ...input.query,
    type: input.organizationId ? FilterConfigCase.OrgPosters : FilterConfigCase.EventPosters,
    ref_id: input.organizationId || input.eventId,
  });

  return httpService
    .get<OrganizationDocumentsFilterResponse>({
      url: `/api/v3/discovery/filter-config/search?${queryParams}`,
    })
    .then(response => {
      const {
        facetFilters: { hits, nbHits, page, hitsPerPage, nbPages },
      } = response.data;
      const items = hits.map(item => ({
        ...item,
        id: item.poster_id,
        keywords: item._highlightResult?.keywords?.map(k => k.value) || [],
      }));
      return {
        items,
        collection: {
          total: nbHits,
          offset: hitsPerPage * page,
          limit: hitsPerPage,
          number_of_pages: nbPages,
        },
      };
    });
};
